import { useEffect, useRef } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { Period } from '../utils/resolution'
import { isIntradayPeriod } from '../utils/resolution'
import type { ChartSettings } from '../components/SettingsModal'
import { useChartThemeColors } from '../components/theme-colors-context'
import { effectiveColor } from '../utils/themedColors'
import type { SessionHighlightConfig } from '../utils/sessionHighlight'
import { SESSION_BREAKS_NAME, type SessionBreaksConfig } from '../utils/sessionBreaks'
import { PREVIOUS_CLOSE_NAME, type PreviousCloseConfig } from '../utils/previousClose'
import {
  PREVIOUS_DAY_HIGH_NAME,
  PREVIOUS_DAY_LOW_NAME,
  type PrevDayLevelConfig,
} from '../utils/previousDayHighLow'
import { DAY_OPEN_NAME, type OpenLineConfig } from '../utils/openLine'
import { CANDLE_PANE_ID } from '../types/chart'

type Chart = ReturnType<ChartManager['getChart']>

/**
 * Manages market session display features on the chart:
 * - Session highlight overlays (pre-market/after-hours shading)
 * - Extended hours data filtering
 * - Previous close reference lines
 *
 * Consolidates three EdgeChart useEffects (L511-573) into one hook.
 */
export function useMarketSession(
  managerRef: React.RefObject<ChartManager | null>,
  chartInstance: Chart | null,
  period: Period,
  settings: ChartSettings,
): void {
  // Session/break overlay colors default to the active THEME PROFILE (per
  // dark/black/light — published through ChartThemeColorsContext by the host),
  // and a user's explicit pick (recorded in `customizedColors`) wins. When no
  // host provider is mounted (the demo app, unit tests) the stored settings
  // value is used verbatim, preserving prior behavior.
  const hostColors = useChartThemeColors()
  const preMarketColor = hostColors
    ? effectiveColor(
        'preMarketColor',
        settings.preMarketColor,
        hostColors,
        settings.customizedColors,
      )
    : settings.preMarketColor
  const afterHoursColor = hostColors
    ? effectiveColor(
        'afterHoursColor',
        settings.afterHoursColor,
        hostColors,
        settings.customizedColors,
      )
    : settings.afterHoursColor
  // A `null` break means the active profile ships no day-break line: the host
  // omits `sessionBreakColor` from the context, so an un-customized key resolves
  // to null and the indicator is skipped (the light profile's "none").
  const breakCustomized = settings.customizedColors?.includes('sessionBreakColor')
  const sessionBreakColor: string | null = breakCustomized
    ? settings.sessionBreakColor
    : hostColors
      ? (hostColors.sessionBreakColor ?? null)
      : settings.sessionBreakColor

  // Session highlight — pre/after-hours shading. Applied by the ChartManager
  // across EVERY pane (candle + sub-panes) so it reads as one continuous
  // background column, drawn beneath the content via a negative zLevel. Intraday
  // only. See ChartManager.setSessionHighlight.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return

    const isIntraday = isIntradayPeriod(period)
    manager.setSessionHighlight(
      settings.extendedHoursHighlight && isIntraday
        ? ({
            preMarketColor,
            afterHoursColor,
            timezone: settings.timezone,
          } satisfies SessionHighlightConfig)
        : null,
    )
  }, [
    managerRef,
    chartInstance,
    period,
    settings.extendedHoursHighlight,
    preMarketColor,
    afterHoursColor,
    settings.timezone,
  ])

  // Session break lines — a vertical dashed line at each new trading day.
  // Intraday only: on daily+ timeframes every bar is its own session, so the
  // chart would be nothing but break lines.
  useEffect(() => {
    if (!chartInstance) return

    chartInstance.removeIndicator(CANDLE_PANE_ID, SESSION_BREAKS_NAME)

    const isIntraday = isIntradayPeriod(period)

    // `sessionBreakColor === null` → this profile ships no break line; skip the
    // indicator even when the toggle is on (unless the user picked a color).
    if (settings.showSessionBreaks && isIntraday && sessionBreakColor) {
      chartInstance.createIndicator(
        {
          name: SESSION_BREAKS_NAME,
          extendData: {
            color: sessionBreakColor,
            timezone: settings.timezone,
          } satisfies SessionBreaksConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [chartInstance, period, settings.showSessionBreaks, sessionBreakColor, settings.timezone])

  // Extended hours data filtering
  const extendedHoursInitRef = useRef(true)
  useEffect(() => {
    // Skip on initial mount — constructor already applied the filter
    if (extendedHoursInitRef.current) {
      extendedHoursInitRef.current = false
      return
    }
    managerRef.current?.setExtendedHours(settings.extendedHours, settings.timezone)
  }, [managerRef, settings.extendedHours, settings.timezone])

  // Split-adjusted vs unadjusted prices. ChartManager.setAdjusted reloads the
  // chart with the new query param; the constructor already wired the initial
  // value, so skip the first run.
  const adjustedInitRef = useRef(true)
  useEffect(() => {
    if (adjustedInitRef.current) {
      adjustedInitRef.current = false
      return
    }
    managerRef.current?.setAdjusted(settings.adjusted)
  }, [managerRef, settings.adjusted])

  // Previous close lines (per-day). Only meaningful on intraday + daily —
  // on weekly/monthly/yearly each bar covers more than one trading session,
  // so the indicator's day-grouping math collapses to "previous bar's close"
  // (= previous week / month / year), which is not a "previous *session*
  // close" and is visually misleading. Suppress the indicator on those
  // periods. (Removal still runs unconditionally so toggling between
  // periods produces a clean slate.)
  const isIntradayOrDaily =
    period.timespan === 'minute' || period.timespan === 'hour' || period.timespan === 'day'
  useEffect(() => {
    if (!chartInstance) return

    chartInstance.removeIndicator(CANDLE_PANE_ID, PREVIOUS_CLOSE_NAME)

    if (settings.showPreviousClose && isIntradayOrDaily) {
      chartInstance.createIndicator(
        {
          name: PREVIOUS_CLOSE_NAME,
          extendData: {
            color: settings.previousCloseColor,
            timezone: settings.timezone,
          } satisfies PreviousCloseConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [
    chartInstance,
    isIntradayOrDaily,
    settings.showPreviousClose,
    settings.previousCloseColor,
    settings.timezone,
  ])

  // Previous-day High / Low lines. Same intraday+daily gate and per-session
  // day-grouping as the previous-close line above (see that comment). Both off
  // by default. Remove-then-conditionally-create so toggling / period changes
  // reset cleanly.
  useEffect(() => {
    if (!chartInstance) return

    chartInstance.removeIndicator(CANDLE_PANE_ID, PREVIOUS_DAY_HIGH_NAME)
    chartInstance.removeIndicator(CANDLE_PANE_ID, PREVIOUS_DAY_LOW_NAME)

    if (!isIntradayOrDaily) return

    if (settings.showPrevDayHigh) {
      chartInstance.createIndicator(
        {
          name: PREVIOUS_DAY_HIGH_NAME,
          extendData: {
            color: settings.prevDayHighColor,
            timezone: settings.timezone,
          } satisfies PrevDayLevelConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
    if (settings.showPrevDayLow) {
      chartInstance.createIndicator(
        {
          name: PREVIOUS_DAY_LOW_NAME,
          extendData: {
            color: settings.prevDayLowColor,
            timezone: settings.timezone,
          } satisfies PrevDayLevelConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [
    chartInstance,
    isIntradayOrDaily,
    settings.showPrevDayHigh,
    settings.prevDayHighColor,
    settings.showPrevDayLow,
    settings.prevDayLowColor,
    settings.timezone,
  ])

  // Current-day open line. Same intraday+daily gate and remove-then-conditionally-create
  // pattern as the reference lines above (see the previous-close comment).
  useEffect(() => {
    if (!chartInstance) return

    chartInstance.removeIndicator(CANDLE_PANE_ID, DAY_OPEN_NAME)

    if (settings.showOpen && isIntradayOrDaily) {
      chartInstance.createIndicator(
        {
          name: DAY_OPEN_NAME,
          extendData: {
            color: settings.openColor,
            timezone: settings.timezone,
          } satisfies OpenLineConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [chartInstance, isIntradayOrDaily, settings.showOpen, settings.openColor, settings.timezone])
}
